import "./styles.scss";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import { useApp } from "context/app";

import ModalNew from "components/ModalNew";
import DetailsCard from "components/DetailsCard";
import DetailsCardList from "components/DetailsCardList";
import DevelopmentMobile from "components/DevelopmentMobile";

import OverviewModal from "../OverviewModal";
import AccessDataModal from "../AccessDataModal";

import api from "libs/api";
import countries from "libs/countries.json";
import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";
import useResponsive from "libs/helpers/useResponsive";
import plans_data from "libs/plans_data";

const mainClass = "website-info-modal";

const planIcons = {
  safety: plans_data[0].icon,
  basic: plans_data[1].icon,
  premium: plans_data[2].icon,
  "premium / e-commerce": plans_data[2].icon,
};

const WebsiteInfoModal = ({ id, isOpen, onClose }) => {
  const { user } = useApp();
  const { t } = useTranslation();
  const { max } = useResponsive();

  const { data, isLoading: loading } = useQuery({
    queryKey: ["client-modal", id],
    queryFn: () => api.get(`/admin/clients/${id}`),
    enabled: !!id,
  });

  const [modal, setModal] = useState(null);

  const plan = data?.plan?.[0];
  const planIcon = planIcons[plan?.name?.trim().toLowerCase()];

  const details_data = [
    {
      title: t("Overview"),
      footer: max.sm ? (
        <DevelopmentMobile
          available={
            toHoursAndMinutes(data?.time_used?.available_in_plan)?.formatted
          }
          left={toHoursAndMinutes(data?.time_used?.time_left)?.formatted}
          exceeding={
            toHoursAndMinutes(data?.time_used?.time_exceeding_plan)?.formatted
          }
        />
      ) : null,
      action: {
        label: t("Edit"),
        onclick: () => setModal("overview"),
      },
      items: [
        {
          type: user?.type === "admin" ? "link" : null,
          label: t("URL Address"),
          value:
            user?.type === "admin"
              ? {
                  link_label: data?.website,
                  link_href: `/clients/${data?._id}`,
                }
              : data?.website,
        },
        {
          type: "status",
          label: "Status",
          value: {
            status: data?.status,
          },
          tooltip:
            data?.status !== 1
              ? t(
                  "Your website is safe and up to date. Your payment method is active.",
                )
              : null,
        },
        {
          label: t("Last CMS update"),
          value: data?.last_cms_update
            ? moment(data?.last_cms_update).format("ll")
            : "-",
        },
        {
          label: t("SSL certificate valid until"),
          value: data?.ssl_valid_until
            ? moment(data?.ssl_valid_until).format("ll")
            : "-",
        },
        {
          label: t("Domain valid until"),
          value: data?.domain_valid_until
            ? moment(data?.domain_valid_until).format("ll")
            : "-",
        },
        {
          label: t("Time available in the plan"),
          value: toHoursAndMinutes(data?.time_used?.available_in_plan)
            ?.formatted,
          hiddenMobile: true,
        },
        {
          label: t("Time used"),
          value: toHoursAndMinutes(data?.time_used?.time_used?.$numberDecimal)
            ?.formatted,
          hiddenMobile: true,
        },
        {
          label: t("Time left"),
          value: toHoursAndMinutes(data?.time_used?.time_left)?.formatted,
          hiddenMobile: true,
        },
        {
          label: t("Time exceeding plan"),
          value: toHoursAndMinutes(data?.time_used?.time_exceeding_plan)
            ?.formatted,
          hiddenMobile: true,
        },
      ],
    },
    ...(!!data?.password1_link
      ? [
          {
            title: "Website access data",
            items: [
              {
                type: "button",
                value: {
                  button_label: "Show access data",
                  variant: "normal",
                  external_href: true,
                  button_href: data?.password1_link,
                },
              },
            ],
          },
        ]
      : []),
  ];

  const payment_data = [
    {
      variantMobile: "plan",
      title: "Plan",
      items: [
        {
          label: t("Type"),
          value: `${data?.plan?.[0]?.name} plan`,
          hiddenMobile: true,
        },
        {
          custom_children: (
            <div className={`${mainClass}__plan-data`}>
              <i>{planIcon}</i>
              <span>{plan?.name}</span>
            </div>
          ),
          hiddenDesktop: true,
        },
      ],
    },
    {
      title: t("Contact data"),
      items: [
        {
          label: t("Full name"),
          value: `${data?.contact_data?.first_name} ${data?.contact_data?.last_name}`,
        },
        {
          type: "link",
          label: t("Email"),
          value: {
            link_label: data?.contact_data?.email,
            link_href: `mailto:${data?.contact_data?.email}`,
          },
        },
        {
          type: "link",
          label: t("Phone"),
          value: {
            link_label: `${
              countries?.find(
                (item) => item?.code === data?.contact_data?.phone?.code,
              )?.dialling_code || ""
            } ${data?.contact_data?.phone?.number}`,
            link_href: `tel:${
              countries?.find(
                (item) => item?.code === data?.contact_data?.phone?.code,
              )?.dialling_code || ""
            } ${data?.contact_data?.phone?.number}`,
          },
        },
      ],
    },
    {
      title: t("Website access data"),
      action: {
        label: t("View / Edit"),
        onclick: () => setModal("access"),
      },
      items: [
        {
          value: t(
            "Here you can update access data to your website. The data includes access to FTP server, database and CMS WordPress.",
          ),
        },
      ],
    },
  ];

  return (
    <>
      <ModalNew
        title={data?.website}
        variant="more-wide"
        dataLoading={loading}
        isOpen={(modal === null || (max.sm && modal !== null)) && isOpen}
        isSubmitHidden
        onClose={onClose}
      >
        <div className={mainClass}>
          <div className={`${mainClass}__col`}>
            {details_data?.map((data, index) => (
              <DetailsCard key={index} {...data}>
                <DetailsCardList items={data?.items} view={data?.view} />
              </DetailsCard>
            ))}
          </div>
          <div className={`${mainClass}__col`}>
            {payment_data?.map((data, index) => (
              <DetailsCard key={index} {...data}>
                <DetailsCardList items={data?.items} view={data?.view} />
              </DetailsCard>
            ))}
          </div>
        </div>
      </ModalNew>

      <OverviewModal
        data={data}
        isOpen={modal === "overview"}
        onClose={() => setModal(null)}
      />
      <AccessDataModal
        website={data?.website}
        isOpen={modal === "access"}
        onClose={() => setModal(null)}
      />
    </>
  );
};

export default WebsiteInfoModal;
